import type { CSSProperties } from 'react';
import { Icon, type IconName } from './icons';

// LA COULEUR D'UN TYPE DE MESSAGE — une teinte par type, reprise de la maquette (`TYPE_HUE`), pour qu'un catalogue,
// une facture ou un avis d'expédition se reconnaissent d'un coup d'œil dans la bibliothèque des mappings, sur la fiche
// d'un fournisseur et dans la fiche d'un mapping.
// ⚠️ La teinte se MÉLANGE au papier et à l'encre (`color-mix`) au lieu d'être écrite pour chaque thème : elle suit donc
//    le thème clair ou sombre toute seule. Les accusés et un type inconnu prennent un gris à peine teinté.

/** Teinte oklch (angle, chroma) et pictogramme de chaque type. */
const TYPE_HUE: Record<string, [number, number, IconName]> = {
  CATALOG: [300, 0.15, 'tag'],
  DESPATCH_ADVICE: [245, 0.14, 'box'],
  ORDER_RESPONSE: [195, 0.11, 'check'],
  INVOICE: [340, 0.15, 'file'],
  REMITTANCE_ADVICE: [118, 0.13, 'card'],
  SALES_REPORT: [48, 0.15, 'chart'],
  INVENTORY_REPORT: [272, 0.12, 'layers'],
  APPLICATION_ACK: [228, 0.05, 'inbound'],
  SYNTAX_ACK: [320, 0.05, 'shield'],
};

/** Les couleurs dérivées d'un type : la teinte pleine, le lavis d'un en-tête, le fond, le filet et l'encre. */
export function typeTint(type: string): { solid: string; wash: string; bg: string; line: string; fg: string } {
  const [h, c] = TYPE_HUE[type] ?? [250, 0.03];
  const solid = `oklch(0.62 ${c} ${h})`;
  return {
    solid,
    wash: `color-mix(in oklch, ${solid} 6%, var(--paper))`,
    bg: `color-mix(in oklch, ${solid} 15%, var(--paper))`,
    line: `color-mix(in oklch, ${solid} 34%, var(--paper))`,
    fg: `color-mix(in oklch, ${solid} 58%, var(--ink-900))`,
  };
}

/** Le pictogramme d'un type. */
export function typeIcon(type: string): IconName {
  return TYPE_HUE[type]?.[2] ?? 'file';
}

/** L'étiquette d'un type : pastille carrée à sa couleur + code. */
export function TypeTag({ type }: { type: string }) {
  const k = typeTint(type);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 22, padding: '0 8px', borderRadius: 'var(--r-xs)', background: k.bg, color: k.fg, border: `1px solid ${k.line}`, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', fontWeight: 700, whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: 2, flexShrink: 0, background: k.solid }} />{type}
    </span>
  );
}

/** La tuile d'un type : son pictogramme sur son fond. */
export function TypeTile({ type, size = 32, style }: { type: string; size?: number; style?: CSSProperties }) {
  const k = typeTint(type);
  return (
    <span aria-hidden style={{ width: size, height: size, borderRadius: 'var(--r-sm)', display: 'grid', placeItems: 'center', flexShrink: 0, background: k.bg, color: k.fg, border: `1px solid ${k.line}`, ...style }}>
      <Icon name={typeIcon(type)} size={size > 34 ? 18 : 15} sw={1.9} />
    </span>
  );
}
